'use client';

import React from 'react';
import { useId } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';

import { CompactRelativeTimestamp } from '@/components/CompactRelativeTimestamp';
import { FileFeedbackButton } from '@/components/FileFeedbackButton';
import { IdentityChipGroup, IdentityRow, identityFromDocProvenance } from '@/components/identity';
import { targetFeedItem } from '@/feedback';
import { ASPECT_REVIEW, ASPECT_QUESTION } from '@/lib/consts';
import { cdnAwareLoader, DISPLAY_IMAGE_WIDTHS, getSizedImageUrl } from '@/lib/displayImage';
import { extractPlainText } from '@/lib/structuredDocUtils';
import { isLLM } from '@/lib/utils';

import { DisplayDoc, DisplayUser, FeedItem } from '../lib/api';

type ThreadFeedItemProps = {
  item: FeedItem;
  showTopic: boolean;
  currentUser?: DisplayUser | null;
};

const getPreviewText = (doc: DisplayDoc, maxLength: number): string => {
  const fullText = doc.structured_content ? extractPlainText(doc.structured_content) : doc.text;
  return fullText.length > maxLength ? `${fullText.substring(0, maxLength)}...` : fullText;
};

export const ThreadFeedItem: React.FC<ThreadFeedItemProps> = ({ item, showTopic, currentUser }) => {
  const threadId = useId();

  if (!item) return null;
  const thread = item.thread;
  if (!thread || thread.docs.length == 0) return null;
  const docs = thread.docs;

  // Find the latest doc from a human user (non-LLM) from the supplied docs
  const latestUserDoc = (() => {
    for (let i = docs.length - 1; i >= 0; i--) {
      const doc = docs[i];
      if (!isLLM(doc.provenance.creator)) {
        return doc;
      }
    }
    // Fallback to the last supplied doc
    return docs[docs.length - 1];
  })();

  // Is there a review or question in the supplied docs?
  const hasReview = docs.some((doc) => doc.aspect?.cref === ASPECT_REVIEW);
  const hasQuestion = docs.some((doc) => doc.aspect?.cref === ASPECT_QUESTION);

  const protectedDocPath = `/doc/${latestUserDoc.id}`;
  const href_target = currentUser
    ? protectedDocPath
    : item.public_review_id
      ? `/review/${item.public_review_id}`
      : `/login?${new URLSearchParams({ redirect: protectedDocPath }).toString()}`;
  const feedbackTargetId = item.summary?.id ?? latestUserDoc.id;
  const topic = latestUserDoc.topic?.display || '';

  const just = item.justification;

  const participantIdentities = Array.from(
    new Map(
      docs
        .map((doc) => identityFromDocProvenance(doc.provenance))
        .filter((identity) => identity.username !== currentUser?.username)
        .filter((identity) => identity.username !== just?.friend)
        .filter((identity) => !isLLM(identity.username))
        .map((identity) => [identity.username, identity])
    ).values()
  );

  /** Derives the header text and whether it contains the document topic. */
  const deriveHeader = (): { text: string; includesDocTopic: boolean } => {
    let result: string = '';
    if (hasReview) {
      result += `Review `;
    } else if (hasQuestion) {
      result += `Question `;
    }
    // For non-logged-in users or feeds with incomplete justification
    if (!currentUser || !just) {
      if (result.length > 0) {
        return { text: `${result}${topic || 'Thread'}`, includesDocTopic: topic.length > 0 };
      }
      return { text: topic || 'Thread', includesDocTopic: topic.length > 0 };
    }

    // For logged-in users with justification
    if (just.mentioner) {
      result += `@${just.mentioner} tagged you`;
    } else if (just.friend) {
      result += `@${just.friend}'s thread`;
    }
    if (result.length === 0 && just.topic) {
      result += `Your interests: ${just.topic.display}`;
      return {
        text: result,
        includesDocTopic: topic.length > 0 && just.topic.cref === latestUserDoc.topic?.cref,
      };
    }
    if (result.length === 0 && just.is_owned) {
      result += `Your thread`;
    }
    if (result.length > 0) {
      return { text: result, includesDocTopic: false };
    }
    return { text: topic || 'Thread', includesDocTopic: topic.length > 0 };
  };

  /** Stores the derived left-side header text and topic inclusion state. */
  const header = deriveHeader();
  /** Indicates whether the right topic renders when enabled, present, and absent from the left header. */
  const shouldShowRightTopic = showTopic && topic.length > 0 && !header.includesDocTopic;

  /** Renders the flexible header row with its optional right topic label. */
  const renderHeader = (): React.ReactNode => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
        <Typography variant="body2" component="span" color="text.secondary">
          {header.text}
        </Typography>

        {participantIdentities.length > 0 ? (
          <IdentityChipGroup
            identities={participantIdentities}
            maxVisible={3}
            chipProps={{ linkMode: 'text', atPrefix: true }}
          />
        ) : null}
      </Box>
      {shouldShowRightTopic ? (
        <Typography
          variant="body2"
          component="span"
          color="text.secondary"
          noWrap
          sx={{ flexShrink: 1, minWidth: 0, maxWidth: '40%' }}
        >
          {topic}
        </Typography>
      ) : null}
    </Box>
  );

  const renderReplyChain = (): React.JSX.Element => {
    return (
      <Box component="section" aria-label="Reply chain" sx={{ mt: 1 }}>
        <List dense disablePadding>
          {docs.slice(-5).map((doc) => {
            const identity = identityFromDocProvenance(doc.provenance);

            return (
              <ListItem
                key={doc.id}
                disableGutters
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5 }}
              >
                <IdentityRow identity={identity} linkMode="text" avatarSize={24} />
                <Typography variant="body2" component="span">
                  {getPreviewText(doc, 450)}
                </Typography>
              </ListItem>
            );
          })}
        </List>
      </Box>
    );
  };

  const findSKUImageHref = (): string | null => {
    // The thumbnail comes from the top-scored offer the backend selected
    // for display in the feed card.
    const firstImage = item.offers[0]?.images[0];
    return firstImage ? getSizedImageUrl(firstImage, DISPLAY_IMAGE_WIDTHS.thumbnail) : null;
  };

  const skuImageHref = findSKUImageHref();

  return (
    <Card sx={{ mb: 1 }}>
      <CardHeader
        title={renderHeader()}
        action={<FileFeedbackButton target={targetFeedItem(feedbackTargetId)} />}
        sx={{
          pb: 1,
          '& .MuiCardHeader-content': {
            width: '100%',
          },
        }}
      />
      <CardActionArea
        component={Link}
        href={href_target}
        aria-label={`View ${showTopic ? topic : 'Thread'}`}
      >
        <CardContent sx={{ pt: 0 }}>
          <Box
            component="article"
            aria-labelledby={`thread-${threadId}`}
            sx={{ display: 'flex', gap: 2 }}
          >
            <Box component={Typography} id={`thread-${threadId}`} sx={visuallyHidden}>
              Thread Content
            </Box>

            {skuImageHref && (
              <Box
                sx={{
                  position: 'relative',
                  width: 100,
                  height: 100,
                  flexShrink: 0,
                  overflow: 'hidden',
                  borderRadius: 1,
                }}
              >
                <Image
                  loader={cdnAwareLoader}
                  src={skuImageHref}
                  alt="Product Image"
                  fill
                  sizes="100px"
                  style={{ objectFit: 'cover' }}
                />
              </Box>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {item.summary ? (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {item.summary.text}
                </Typography>
              ) : (
                <>
                  <Typography variant="body1" component="div">
                    <Typography component="span" fontWeight="bold">
                      {latestUserDoc.provenance.creator}
                    </Typography>{' '}
                    <CompactRelativeTimestamp timestamp={latestUserDoc.provenance.createdon} />{' '}
                    <Typography component="span" fontWeight="bold">
                      :
                    </Typography>{' '}
                    {getPreviewText(latestUserDoc, 450)}
                  </Typography>
                  {docs.length > 1 && renderReplyChain()}
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
